import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEARCH_LIMIT, useClientSearch } from '../api/search.ts';
import { LoadFailure, isSessionFailure } from './LoadFailure.tsx';
import { HeldMarker, StatusTag } from './StatusTag.tsx';
import { ClientLookup } from './ClientLookup.tsx';
import { useFacilitySearch, type FacilityMatch } from '../api/facilityPage.ts';
import { useHomeSearch, type PlaceMatch } from '../api/quickSearch.ts';
import { useToolbar } from '../api/navigation.ts';

/** Buildings the top bar offers beneath the clients: enough to arrive at one, never a list. */
const BUILDINGS_SHOWN = 4;
/** Homes, by name or street, beneath the buildings (13 October). */
const HOMES_SHOWN = 4;
import type { ClientListRow } from '../api/types.ts';

const DEBOUNCE_MS = 250;

/**
 * Find a client from anywhere: the top bar's typeahead.
 *
 * Searches as you type, a beat after the last keystroke, over the same
 * `GET /clients` the client list uses, so a former name finds the client as it
 * does there. Arrow keys move through the matches, Enter opens the highlighted
 * one, Escape closes the list.
 *
 * **A failed search never reads as "no matches".** That is the bug the client
 * list already had and lost: a refused session rendered as an empty list, a
 * false and calm statement about the data. So:
 *
 *  - a 401, or a 403 from the authenticator, says the session is not valid;
 *  - any other failure says the search could not be run;
 *  - "No matches" appears only for a successful answer, to this exact text,
 *    with zero rows.
 *
 * The previous matches stay on screen while the next search runs, so the list
 * does not blank on every keystroke; but they are the previous text's matches,
 * so Enter will not open one until the current search has answered, and a
 * failure takes the panel even with them in hand.
 */
export function ClientSearch({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // A home with several residents, opened by somebody who cannot open the
  // visits board: its residents the reader holds, listed here (the 13 October
  // dead end; Ctrl+F already did this).
  const [drilled, setDrilled] = useState<PlaceMatch | null>(null);
  const toolbar = useToolbar();
  const canOpenBoard = toolbar.data?.modules.some((m) => m.key === 'visits') ?? false;

  useEffect(() => setDrilled(null), [typed]);

  useEffect(() => {
    const timer = setTimeout(() => setQ(typed.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => setActive(0), [q]);

  // Close on a press anywhere outside, not on blur: a blur fires before the
  // click on a match lands, and would close the list out from under it.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const search = useClientSearch(q);
  const text = typed.trim();
  const failure = search.error ?? (search.isPlaceholderData ? search.failureReason : null);
  const takeover =
    failure !== null && (isSessionFailure(failure) || !search.data || search.isPlaceholderData);
  // An answer to what is in the box now, not to what was in it a moment ago.
  const answered = q !== '' && q === text && search.isSuccess && !search.isPlaceholderData;
  const rows: ClientListRow[] = takeover || q === '' ? [] : search.data?.clients ?? [];
  const total = search.data?.total ?? 0;
  // BUILDINGS (8 October): searching for a building arrives at its page. The
  // answer is the building's name, type and address and nothing about who is
  // there; the page decides what of that the reader may see.
  const places = useFacilitySearch(q, BUILDINGS_SHOWN);
  const buildings: FacilityMatch[] =
    takeover || q.length < 2 || q !== text ? [] : places.data?.facilities ?? [];
  // HOMES (13 October): a client's home by its name or its street. The top bar
  // asked only for clients and buildings, so a street found nothing here while
  // Ctrl+F, which asks GET /places, found the home. Row security narrows the
  // answer to the caller's own clients' homes, as on the board.
  const homeSearch = useHomeSearch(q, HOMES_SHOWN);
  const homes: PlaceMatch[] =
    takeover || q.length < 2 || q !== text ? [] : homeSearch.data?.places ?? [];
  const optionCount = rows.length + buildings.length + homes.length;

  const openBuilding = (b: FacilityMatch) => {
    setOpen(false);
    setTyped('');
    setQ('');
    navigate(`/facilities/${b.id}`);
  };

  // One resident: their record. More than one: their tile on the visits board,
  // for a reader who can open the board; for anyone else, the residents the
  // reader holds, listed here, never a page that refuses them.
  const openHome = (h: PlaceMatch) => {
    if (h.clients.length !== 1 && !canOpenBoard) {
      setDrilled(h);
      setActive(0);
      return;
    }
    setOpen(false);
    setTyped('');
    setQ('');
    if (h.clients.length === 1) navigate(`/clients/${h.clients[0]!.client_id}`, { state: {} });
    // The card to reveal in history state, never the URL: a place key can be free text (url-guard.ts).
    else navigate('/visits/board?view=locations', { state: { reveal: `location:${h.location_key}` } });
  };

  const openResident = (clientId: string) => {
    setOpen(false);
    setTyped('');
    setQ('');
    setDrilled(null);
    navigate(`/clients/${clientId}`, { state: {} });
  };

  const openClient = (row: ClientListRow) => {
    setOpen(false);
    setTyped('');
    setQ('');
    // The typeahead can be used from anywhere, so the label names wherever that
    // was rather than guessing: back is still back, it just says "Back".
    navigate(`/clients/${row.client_id}`, { state: {} });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (drilled) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, drilled.clients.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); const c = drilled.clients[active]; if (c) openResident(c.client_id); }
      else if (e.key === 'Escape') { e.preventDefault(); setDrilled(null); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (optionCount > 0) setActive((i) => Math.min(i + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (answered && rows[active]) openClient(rows[active]);
      else if (answered && buildings[active - rows.length]) openBuilding(buildings[active - rows.length]!);
      else if (answered && homes[active - rows.length - buildings.length]) openHome(homes[active - rows.length - buildings.length]!);
      // Typed faster than the debounce: run the search now rather than open a
      // match for text that is no longer in the box.
      else if (text !== q) setQ(text);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showPanel = open && text !== '';
  const optionId = (i: number) => `${listId}-option-${i}`;

  // Buildings follow the clients in one keyboard order: option rows.length + i.
  const buildingList = answered && buildings.length > 0 && (
    <div className="border-t border-neutral-200">
      <p className="px-3 pt-2 pb-1 text-[0.6875rem] font-bold tracking-[0.14em] text-neutral-600 uppercase">Facilities</p>
      <ul role="listbox" aria-label="Matching facilities" className="pb-1">
        {buildings.map((b, i) => {
          const at = rows.length + i;
          return (
            <li
              key={b.id}
              id={optionId(at)}
              role="option"
              aria-selected={at === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(at)}
              onClick={() => openBuilding(b)}
              className={`min-h-[44px] cursor-pointer px-3 py-2 ${at === active ? 'bg-neutral-100' : ''}`}
            >
              <p className="truncate font-semibold text-neutral-900">{b.facility_name}</p>
              <p className="truncate text-sm text-neutral-600">
                {[b.residence_type_label ?? b.facility_type, [b.street_address, b.city].filter(Boolean).join(', ')]
                  .filter(Boolean).join(' · ') || 'No address recorded'}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );

  // Homes follow the buildings in the same keyboard order: option rows.length + buildings.length + i.
  const homeList = answered && homes.length > 0 && (
    <div className="border-t border-neutral-200">
      <p className="px-3 pt-2 pb-1 text-[0.6875rem] font-bold tracking-[0.14em] text-neutral-600 uppercase">Homes</p>
      <ul role="listbox" aria-label="Matching homes" className="pb-1">
        {homes.map((h, i) => {
          const at = rows.length + buildings.length + i;
          return (
            <li
              key={h.location_key}
              id={optionId(at)}
              role="option"
              aria-selected={at === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(at)}
              onClick={() => openHome(h)}
              className={`min-h-[44px] cursor-pointer px-3 py-2 ${at === active ? 'bg-neutral-100' : ''}`}
            >
              <p className="truncate font-semibold text-neutral-900">{h.location}</p>
              <p className="truncate text-sm text-neutral-600">{h.location_address ?? 'No address recorded'}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div ref={box} className={`relative ${className}`}>
      <label className="sr-only" htmlFor={`${listId}-input`}>
        Find a client by name, reference or former name, a facility by name or street, or a home by its name or street
      </label>
      <input
        id={`${listId}-input`}
        type="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={`${listId}-list`}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && answered && active < optionCount ? optionId(active) : undefined}
        autoComplete="off"
        inputMode="search"
        value={typed}
        placeholder="Find a client, facility or home"
        onChange={(e) => {
          setTyped(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="min-h-[44px] w-full rounded-lg border border-neutral-300 bg-white px-3 text-base
                   text-neutral-900"
      />

      {showPanel && (
        <div
          id={`${listId}-list`}
          className="absolute top-full right-0 left-0 z-30 mt-1 max-h-[70vh] overflow-y-auto rounded-lg
                     border border-neutral-200 bg-white shadow-lg"
        >
          {drilled ? (
            <div>
              <button type="button" onClick={() => setDrilled(null)}
                      className="flex min-h-[44px] w-full items-center px-3 text-left text-sm font-semibold text-neutral-600 hover:text-neutral-900">
                ← Back to the matches
              </button>
              <p className="px-3 pb-1 text-[0.6875rem] font-bold tracking-[0.14em] text-neutral-600 uppercase">
                Your clients at {drilled.location}
              </p>
              <ul role="listbox" aria-label={`Your clients at ${drilled.location}`} className="pb-1">
                {drilled.clients.map((c, i) => (
                  <li key={c.client_id} id={optionId(i)} role="option" aria-selected={i === active}
                      onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)}
                      onClick={() => openResident(c.client_id)}
                      className={`flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 ${i === active ? 'bg-neutral-100' : ''}`}>
                    <span className="min-w-0 flex-1 truncate font-semibold text-neutral-900">{c.display_name}</span>
                    <span className="shrink-0 font-mono text-sm text-neutral-600 tabular-nums">{c.client_ref}</span>
                  </li>
                ))}
              </ul>
              {drilled.client_count > drilled.clients.length && (
                <p className="border-t border-neutral-200 px-3 py-2 text-sm text-neutral-500">
                  Showing {drilled.clients.length} of your {drilled.client_count} clients there.
                </p>
              )}
            </div>
          ) : failure && takeover ? (
            <LoadFailure
              what="The search"
              verb="could not be run"
              error={failure}
              onRetry={() => void search.refetch()}
              retrying={search.isFetching}
            />
          ) : answered && rows.length === 0 ? (
            <>
              <p className="px-3 py-3 text-[0.9375rem] text-neutral-600">
                No {buildings.length + homes.length > 0 ? 'clients match' : 'matches for'} “{q}”. Former names are searched too.
              </p>
              {buildingList}
              {homeList}
            </>
          ) : rows.length === 0 ? (
            <p className="px-3 py-3 text-[0.9375rem] text-neutral-600">Searching…</p>
          ) : (
            <>
              {/* Words, not a fade, say these are the previous text's matches (15 October). */}
              {!answered && <p className="px-3 pt-2 text-sm text-neutral-600">Searching…</p>}
              <ul role="listbox" aria-label="Matching clients" className="py-1">
                {rows.map((row, i) => (
                  <li
                    key={row.client_id}
                    id={optionId(i)}
                    role="option"
                    aria-selected={answered && i === active}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => openClient(row)}
                    className={`flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 ${
                      answered && i === active ? 'bg-neutral-100' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-neutral-900">{row.display_name}</p>
                      <p className="truncate text-sm text-neutral-600">
                        {row.whereabouts_unknown ? 'Whereabouts unknown' : row.where_to_find_them ?? 'Not recorded'}
                      </p>
                    </div>
                    {/* Two clients can share a name; a status picks the right one (8 October). */}
                    <StatusTag status={row.legal_status} />
                    <HeldMarker held={row.held_at_load} />
                    {row.critical_flags > 0 && (
                      <span className="shrink-0 rounded bg-critical-bg px-2 py-0.5 text-xs font-bold tracking-wide
                                       text-critical-fg uppercase">
                        Critical
                      </span>
                    )}
                    <span className="shrink-0 font-mono text-sm text-neutral-600 tabular-nums">{row.client_ref}</span>
                  </li>
                ))}
              </ul>
              {answered && total > rows.length && (
                <p className="border-t border-neutral-200 px-3 py-2 text-sm text-neutral-500">
                  Showing the first {SEARCH_LIMIT} of {total}. Keep typing to narrow it.
                </p>
              )}
              {buildingList}
              {homeList}
            </>
          )}
          {/* Not one of yours: who holds them (case managers and above; renders nothing otherwise). */}
          {!(failure && takeover) && (
            <ClientLookup
              text={text}
              onOpen={() => {
                setOpen(false);
                setTyped('');
                setQ('');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
